/**
 * "Entwurfs-Vorschau" — the right-hand panel.
 *
 * EVERY value here comes from the read-only preflight endpoint, which runs the real
 * create pipeline's write-free prefix. Nothing is reconstructed in the browser:
 * the title, the billed window, the position texts (including the partial-month
 * range), the amounts, the account/tax ids and `ready` are all backend output.
 *
 * The "Leistungszeitraum" shown here is Vantio's OWN service period. Bexio has no
 * writable service-period field, so this is what the position TEXT carries — the
 * label says so rather than implying Bexio stores it natively.
 */
import React from "react";

import BexioDraftStatusBadge from "./BexioDraftStatusBadge";
import {
  CHECK_LABEL,
  ROW_STATUS,
  blockerText,
  checkState,
  formatChf,
  formatSwissDate,
} from "./bexioDraftLabels";

function Field({ label, children }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <dt className="shrink-0 text-xs text-muted-foreground">{label}</dt>
      <dd className="min-w-0 text-right text-sm text-foreground">{children}</dd>
    </div>
  );
}

function Shell({ children }) {
  return (
    <div className="rounded-xl border border-input bg-card p-6 shadow-sm">
      {children}
    </div>
  );
}

export default function BexioDraftPreviewPanel({
  preflight,
  hasSelection,
  checking,
  error,
  status,
  documentNr,
  onCreate,
  onCheck,
  canCreate,
  createBlockedReason,
  creating,
}) {
  if (!hasSelection) {
    return (
      <Shell>
        <p className="text-sm text-muted-foreground">
          Wählen Sie links ein Mietverhältnis, um seinen Entwurf anzusehen.
        </p>
      </Shell>
    );
  }

  if (checking) {
    return (
      <div className="rounded-xl border border-input bg-card p-4 shadow-sm">
        <div className="h-4 w-32 animate-pulse rounded bg-muted" />
        <div className="mt-4 space-y-2">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="h-4 animate-pulse rounded bg-muted" />
          ))}
        </div>
        <p className="mt-4 text-xs text-muted-foreground">Prüfung läuft …</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800 shadow-sm dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
        <div className="font-medium">Prüfung fehlgeschlagen</div>
        <p className="mt-1">{error}</p>
        <button
          type="button"
          onClick={onCheck}
          className="mt-3 rounded-lg border border-red-300 px-3 py-1.5 text-xs font-medium dark:border-red-500/40"
        >
          Erneut prüfen
        </button>
      </div>
    );
  }

  // Selected but never checked. Deliberately NOT a fetch trigger: selecting a row
  // is a navigation act, and a screen that silently ran a pipeline every time the
  // user looked at something taught them that looking changes state.
  if (!preflight) {
    return (
      <Shell>
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold text-foreground">
            Entwurfs-Vorschau
          </h2>
          <BexioDraftStatusBadge status={status || ROW_STATUS.NOT_CHECKED} />
        </div>
        <p className="mt-3 text-sm text-muted-foreground">
          Dieser Rechnungsentwurf wurde noch nicht technisch geprüft.
        </p>
        <button
          type="button"
          onClick={onCheck}
          className="mt-4 w-full rounded-lg border border-input bg-background px-4 py-2.5 text-sm font-semibold text-foreground hover:bg-muted"
        >
          Entwurf prüfen
        </button>
        <p className="mt-2 text-center text-xs text-muted-foreground">
          Die Prüfung ist schreibgeschützt und erstellt nichts in Bexio.
        </p>
      </Shell>
    );
  }

  const checks = preflight.technical_checks || {};
  const positions = preflight.positions || [];
  const blockers = preflight.blockers || [];
  const period = positions.length
    ? `${formatSwissDate(positions[0].period_start)} – ${formatSwissDate(
        positions[0].period_end
      )}`
    : "—";
  // `ready` is the backend's verdict and the only thing that may arm this button.
  // The extra conditions can only ever SUBTRACT from it — never add.
  //
  // `status` is in here so this gate agrees with the table's. The preflight cannot
  // see the ledger by design, so it answers `ready: true` again for a row whose last
  // attempt ended indeterminate and left the ledger at `creating` with no document
  // number. Without the status term this button would re-arm on exactly the row the
  // table refuses — and the one the panel itself labels "braucht eine manuelle
  // Prüfung" two lines further down.
  const createEnabled =
    canCreate &&
    preflight.ready === true &&
    status === ROW_STATUS.READY &&
    !creating &&
    !documentNr;

  return (
    <div className="rounded-xl border border-input bg-card shadow-sm">
      <div className="flex items-center justify-between gap-3 border-b border-input p-4">
        <h2 className="text-sm font-semibold text-foreground">Entwurfs-Vorschau</h2>
        <BexioDraftStatusBadge status={status} />
      </div>

      <div className="p-4">
        <h3 className="text-base font-semibold text-foreground">
          {preflight.title || "—"}
        </h3>

        <dl className="mt-2 divide-y divide-input">
          <Field label="Leistungszeitraum">{period}</Field>
          <Field label="Ansprechpartner">
            {preflight.target_user_id != null ? (
              // Name if this org configured one, otherwise the bare id. No id->name
              // table here on purpose: a Bexio user id is per-mandant, so any mapping
              // the browser held would be a guess about a different account's people.
              <span
                title={`Bexio-Benutzer-ID ${preflight.target_user_id} aus der Konfiguration`}
              >
                {preflight.target_user_display_name ||
                  `Bexio-Benutzer #${preflight.target_user_id}`}
              </span>
            ) : (
              "—"
            )}
          </Field>
          {/* The two letterhead halves, in the order they appear on the document:
              the header introduces the line items below, the footer closes them.
              Both are shown as the operator TYPED them — plain text with real line
              breaks, never the escaped `<br />` form that goes on the wire. An
              operator cannot verify an encoding they never wrote. */}
          <Field label="Kopfzeile">
            {preflight.header_text ? (
              <span className="whitespace-pre-line">{preflight.header_text}</span>
            ) : (
              <span className="text-muted-foreground">Nicht konfiguriert</span>
            )}
          </Field>
          <Field label="Fusszeile">
            {preflight.footer_text ? (
              <span className="whitespace-pre-line">{preflight.footer_text}</span>
            ) : (
              <span className="text-muted-foreground">Nicht konfiguriert</span>
            )}
          </Field>
        </dl>

        {/* Positionen */}
        <h4 className="mt-5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Positionen
        </h4>
        <table className="mt-2 w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-input text-left">
              <th scope="col" className="py-1.5 font-medium text-muted-foreground">
                Bezeichnung
              </th>
              <th scope="col" className="py-1.5 text-right font-medium text-muted-foreground">
                Betrag
              </th>
            </tr>
          </thead>
          <tbody>
            {positions.length === 0 ? (
              <tr>
                <td colSpan={2} className="py-2 text-muted-foreground">
                  Keine Positionen
                </td>
              </tr>
            ) : (
              positions.map((p, i) => (
                <tr key={`${p.revenue_type}-${i}`} className="border-b border-input">
                  {/* Backend text verbatim — it already carries the partial-month range. */}
                  <td className="py-2 pr-3 text-foreground">{p.text}</td>
                  <td className="py-2 text-right tabular-nums text-foreground">
                    {formatChf(p.amount_chf)}
                  </td>
                </tr>
              ))
            )}
            <tr>
              <td className="py-2 font-medium text-foreground">Total</td>
              <td className="py-2 text-right font-semibold tabular-nums text-foreground">
                {formatChf(preflight.total_amount_chf)}
              </td>
            </tr>
          </tbody>
        </table>

        {/* Technische Prüfungen */}
        <h4 className="mt-5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Technische Prüfungen
        </h4>
        <ul className="mt-2 space-y-1">
          {Object.keys(CHECK_LABEL).map((key) => {
            // Three states, not two. A check the pipeline never reached is neutral:
            // a red cross there would report a defect nobody has observed, and a
            // green tick would certify one nobody has verified.
            const state = checkState(preflight, key);
            const glyph =
              state === "passed" ? "✓" : state === "failed" ? "✕" : "–";
            const glyphTone =
              state === "passed"
                ? "text-emerald-600 dark:text-emerald-400"
                : state === "failed"
                ? "text-red-600 dark:text-red-400"
                : "text-muted-foreground";
            const textTone =
              state === "failed"
                ? "text-red-700 dark:text-red-300"
                : state === "not_evaluated"
                ? "text-muted-foreground"
                : "text-foreground";
            const srText =
              state === "passed"
                ? "erfüllt"
                : state === "failed"
                ? "nicht erfüllt"
                : "nicht geprüft";
            return (
              <li key={key} className="flex items-center gap-2 text-sm">
                <span aria-hidden="true" className={glyphTone}>
                  {glyph}
                </span>
                <span className={textTone}>{CHECK_LABEL[key]}</span>
                {state === "not_evaluated" && (
                  <span className="text-xs text-muted-foreground">
                    (nicht geprüft)
                  </span>
                )}
                <span className="sr-only">{srText}</span>
              </li>
            );
          })}
        </ul>

        {blockers.length > 0 && (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-500/30 dark:bg-amber-500/10">
            <div className="text-xs font-semibold text-amber-800 dark:text-amber-300">
              Zu klären
            </div>
            <ul className="mt-1 list-disc space-y-1 pl-4 text-xs text-amber-900 dark:text-amber-200">
              {blockers.map((b) => (
                <li key={b}>{blockerText(b)}</li>
              ))}
            </ul>
          </div>
        )}

        {documentNr && (
          <p className="mt-4 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800 dark:border-blue-500/30 dark:bg-blue-500/10 dark:text-blue-300">
            In Bexio erstellt: <strong>{documentNr}</strong>
          </p>
        )}

        <button
          type="button"
          onClick={onCreate}
          disabled={!createEnabled}
          className="mt-4 w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {creating ? "Wird erstellt …" : "Entwurf in Bexio erstellen"}
        </button>
        {!canCreate && (
          <p className="mt-2 text-center text-xs text-muted-foreground">
            {createBlockedReason === "ledger"
              ? "Erstellen ist gesperrt, solange die Ledger-Daten unvollständig sind."
              : "Erstellen ist Administratoren vorbehalten."}
          </p>
        )}
        {status === ROW_STATUS.REVIEW && (
          <p className="mt-2 text-center text-xs text-amber-700 dark:text-amber-400">
            Dieser Vorgang braucht eine manuelle Prüfung.
          </p>
        )}
      </div>
    </div>
  );
}
